import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  Settings, 
  Mail, 
  Trash2, 
  CheckSquare, 
  Calendar,
  X,
  MoreHorizontal,
  CalendarIcon
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export function BulkActionSheet({
  selectedTasks,
  onBulkEdit,
  onClearSelection,
  onBulkComplete,
  onBulkDelete,
  onBulkDefer,
  onBulkEmail,
  permissions,
  isVisible = false,
  canDefer = false,
  isSubmitting = false,
}) {
  const [deferDate, setDeferDate] = useState(null);
  const [isDeferPopoverOpen, setIsDeferPopoverOpen] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null); // 'complete', 'delete', 'defer'
  const [selectedDeferDate, setSelectedDeferDate] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  if (!isVisible || selectedTasks.length === 0) {
    return null;
  }

  const canBulkEdit = permissions.can_bulk_edit_own_tasks || 
                      permissions.can_bulk_edit_department_tasks || 
                      permissions.can_bulk_edit_all_tasks;

  const handleDefer = (date) => {
    setDeferDate(date);
    setIsDeferPopoverOpen(false);
    setSelectedDeferDate(date);
    setIsAnimating(true);
    setTimeout(() => {
      setConfirmationType('defer');
      setIsAnimating(false);
    }, 150);
  };

  const handleComplete = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setConfirmationType('complete');
      setIsAnimating(false);
    }, 150);
  };

  const handleDelete = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setConfirmationType('delete');
      setIsAnimating(false);
    }, 150);
  };

  const handleConfirm = async () => {
    switch (confirmationType) {
      case 'complete':
        await onBulkComplete();
        break;
      case 'delete':
        await onBulkDelete();
        break;
      case 'defer':
        await onBulkDefer(selectedDeferDate);
        break;
    }
    setConfirmationType(null);
    setSelectedDeferDate(null);
  };

  const handleCancel = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setConfirmationType(null);
      setSelectedDeferDate(null);
      setDeferDate(null);
      setIsAnimating(false);
    }, 150);
  };

    return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3 min-w-[300px]">
        {/* Selection count */}
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
            {selectedTasks.length}
          </div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            selected
          </span>
        </div>

        <div className="flex-1" />

                 {/* Show confirmation UI or action buttons */}
         <div className="flex items-center gap-2 overflow-hidden">
           {confirmationType ? (
             <div className={`flex items-center gap-2 transform transition-all duration-300 ease-out ${
               isAnimating ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
             }`}>
               <div className="text-sm text-gray-600 dark:text-gray-400">
                 {confirmationType === 'complete' && `Complete ${selectedTasks.length} task${selectedTasks.length !== 1 ? 's' : ''}?`}
                 {confirmationType === 'delete' && `Delete ${selectedTasks.length} task${selectedTasks.length !== 1 ? 's' : ''}?`}
                 {confirmationType === 'defer' && `Defer ${selectedTasks.length} task${selectedTasks.length !== 1 ? 's' : ''} to ${selectedDeferDate ? format(selectedDeferDate, "PPP") : 'selected date'}?`}
               </div>
               <Button
                 size="sm"
                 variant="outline"
                 onClick={handleCancel}
                 className="h-8 px-3"
               >
                 Cancel
               </Button>
                               <Button
                  size="sm"
                  variant={confirmationType === 'delete' ? 'destructive' : 'default'}
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                  className={`h-8 px-3 ${
                    confirmationType === 'complete' 
                      ? 'bg-green-500 hover:bg-green-600 text-white' 
                      : confirmationType === 'delete'
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {confirmationType === 'complete' && 'Completing...'}
                      {confirmationType === 'delete' && 'Deleting...'}
                      {confirmationType === 'defer' && 'Deferring...'}
                    </div>
                  ) : (
                    <>
                      {confirmationType === 'complete' && 'Complete'}
                      {confirmationType === 'delete' && 'Delete'}
                      {confirmationType === 'defer' && 'Defer'}
                    </>
                  )}
                </Button>
             </div>
           ) : (
             <div className={`flex items-center gap-2 transform transition-all duration-300 ease-out ${
               isAnimating ? '-translate-x-full opacity-0' : 'translate-x-0 opacity-100'
             }`}>
               {canBulkEdit && (
                 <Button
                   size="sm"
                   variant="outline"
                   onClick={onBulkEdit}
                   className="h-8 px-3 dark:border-white"
                 >
                   <Settings className="h-4 w-4 mr-1" />
                   Edit
                 </Button>
               )}

               <Button
                 size="sm"
                 variant="outline"
                 onClick={handleComplete}
                 className="h-8 px-3 hidden sm:flex text-green-500 border-green-500 hover:bg-green-500 hover:text-white"
               >
                 <CheckSquare className="h-4 w-4 mr-1" />
                 Complete
               </Button>

               {canDefer && (
                 <Popover open={isDeferPopoverOpen} onOpenChange={setIsDeferPopoverOpen}>
                   <PopoverTrigger asChild>
                     <Button
                       size="sm"
                       variant="outline"
                       className="h-8 px-3 hidden sm:flex text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white"
                     >
                       <Calendar className="h-4 w-4 mr-1" />
                       Defer
                     </Button>
                   </PopoverTrigger>
                   <PopoverContent className="w-auto p-0" align="center">
                     <CalendarComponent
                       mode="single"
                       selected={deferDate}
                       onSelect={handleDefer}
                       disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                       initialFocus
                     />
                   </PopoverContent>
                 </Popover>
               )}

               <Button
                 size="sm"
                 variant="outline"
                 onClick={handleDelete}
                 className="h-8 px-3 hidden lg:flex text-red-500 border-red-500 hover:bg-red-500 hover:text-white"
               >
                 <Trash2 className="h-4 w-4 mr-1" />
                 Delete
               </Button>

               {/* Clear selection */}
               <Button
                 size="sm"
                 variant="ghost"
                 onClick={onClearSelection}
                 className="h-8 px-2"
               >
                 <X className="h-4 w-4" />
               </Button>
             </div>
           )}
         </div>
       </div>
     </div>
   );
 } 